import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import VideoEditingService, {
  ResolvedSource, LoadRequest, SentScript, EditPlanSummary, EDIT_PLAN_STATUS,
} from 'services/VideoEditingService';
import useActorName from 'shared/hooks/useActorName';

const videoEditingService = new VideoEditingService();

type Mode = 'drive' | 'script';

// Which projects the picker searches. 'drafts' is the default: a finished project is
// not what you are looking for when you open this field, and it buries the ones you
// are still working on.
type StatusFilter = 'drafts' | 'ready' | 'all';

const projectLabel = (p: EditPlanSummary) => p.name || 'Untitled project';
const isReadyProject = (p: EditPlanSummary) => p.status === EDIT_PLAN_STATUS.READY;

// MUI's own option matcher, so hiding finished projects does not change how typing
// matches the ones that remain — the status filter composes with the stock text match
// rather than replacing it.
const matchProjects = createFilterOptions<EditPlanSummary>();

interface SourceSelectorProps {
  loadingClips: boolean;
  onLoadClips: (req: LoadRequest) => void;
}

// Pull the backend's actionable message out of an axios error, falling back to
// the generic message.
function errorMessage(err: any): string {
  return err?.response?.data?.error || err?.message || 'Something went wrong';
}

export default function SourceSelector({ loadingClips, onLoadClips }: SourceSelectorProps) {
  const [mode, setMode] = useState<Mode>('drive');
  const [actor] = useActorName();

  // Drive-folder mode.
  const [driveUrl, setDriveUrl] = useState('');

  // Saved projects (independent of the asset locators), loaded once on mount.
  // They come back newest-first; we re-order so the current user's are grouped first.
  const [projects, setProjects] = useState<EditPlanSummary[] | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<EditPlanSummary | null>(null);
  // Filter the saved-projects list to one creator ('' = everyone).
  const [creatorFilter, setCreatorFilter] = useState('');
  // Finished projects are hidden until asked for. Deliberately NOT persisted, so
  // "hidden by default" means the same thing on every visit rather than "whatever was
  // picked last time" — a short list should never have an invisible cause. The choice
  // does survive the picker collapsing, since this component stays mounted.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('drafts');
  // The typed query, controlled so the count of what the filter is hiding can be
  // derived from it (see hiddenReadyCount).
  const [projectInput, setProjectInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    (async () => {
      const pr = await videoEditingService.listAllEditPlans();
      if (cancelled) return;
      setProjects(pr?.plans || []);
      setProjectsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Current user's projects first, then other creators (alphabetical), each
  // newest-first — so the Autocomplete's groups read "Your projects" at the top.
  const projectGroup = (p: EditPlanSummary) => (actor && p.createdBy === actor ? 'Your projects' : (p.createdBy || 'Unknown'));
  const sortedProjects = [...(projects || [])].sort((a, b) => (
    (projectGroup(a) === 'Your projects' ? 0 : 1) - (projectGroup(b) === 'Your projects' ? 0 : 1)
    || projectGroup(a).localeCompare(projectGroup(b))
    || (b.updatedAt || '').localeCompare(a.updatedAt || '')
  ));
  // Distinct creators present in the list (for the filter), and the filtered view.
  const creators = Array.from(new Set((projects || []).map((p) => p.createdBy || 'Unknown'))).sort();
  const filteredProjects = creatorFilter
    ? sortedProjects.filter((p) => (p.createdBy || 'Unknown') === creatorFilter)
    : sortedProjects;

  // Text match first, status second, so the "how many did the filter hide" count below
  // is about what the user actually typed rather than the whole library.
  const textMatched = matchProjects(filteredProjects, {
    inputValue: projectInput,
    getOptionLabel: projectLabel,
  });
  // Excludes READY rather than keeping only DRAFT: a project whose status is missing or
  // unrecognised stays visible instead of silently disappearing from the only place it
  // can be opened from.
  const visibleProjects = statusFilter === 'all'
    ? textMatched
    : textMatched.filter((p) => (statusFilter === 'ready' ? isReadyProject(p) : !isReadyProject(p)));
  // What the default filter is holding back from THIS search. Surfaced in the dropdown
  // (below), because a hidden project that a search would otherwise have found reads as
  // a deleted one — and every project starts out being findable here.
  const hiddenReadyCount = statusFilter === 'drafts' ? textMatched.filter(isReadyProject).length : 0;
  const readyCount = (projects || []).filter(isReadyProject).length;

  // One shared action for both reveal affordances. preventDefault on mousedown keeps the
  // Autocomplete from blurring and closing the popup before the click lands.
  const showReadyButton = (
    <Button
      size="small"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setStatusFilter('all')}
      sx={{ textTransform: 'none' }}
    >
      Show ready
    </Button>
  );

  // Open a saved project directly: rebuild its source (DRIVE or STORYBOARD) and
  // ask the editor to open that specific plan once its clips load.
  function handleSelectProject(p: EditPlanSummary | null) {
    setSelectedProject(p);
    if (!p || !p.driveFolderId) return;
    if (p.sourceType === 'STORYBOARD' && p.storyboardId) {
      onLoadClips({
        sourceType: 'STORYBOARD',
        folderId: p.driveFolderId,
        folderUrl: p.driveFolderUrl || undefined,
        storyboardId: p.storyboardId,
        deliveryId: p.deliveryId || undefined,
        label: p.name || 'Project',
        openPlanId: p.planId,
      });
    } else {
      onLoadClips({
        sourceType: 'DRIVE',
        driveUrl: p.driveFolderUrl || p.driveFolderId,
        folderId: p.driveFolderId,
        folderUrl: p.driveFolderUrl || undefined,
        label: p.name || 'Project',
        openPlanId: p.planId,
      });
    }
  }

  // Sent-script mode. `storyboards` holds the sent-script summaries (one per
  // script that was sent to a creator).
  const [storyboards, setStoryboards] = useState<SentScript[] | null>(null);
  const [storyboardsLoading, setStoryboardsLoading] = useState(false);
  const [storyboardsError, setStoryboardsError] = useState<string | null>(null);
  const [selectedStoryboard, setSelectedStoryboard] = useState<SentScript | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ResolvedSource | null>(null);
  const resolveRequestId = useRef(0);

  // Load the sent-script list the first time the editor opens that tab. This hits
  // a dedicated endpoint that reads CI_SCRIPT_DELIVERIES directly (one row per
  // sent script), so there's no scanning storyboard history or client-side filter.
  //
  // Deps are [mode, storyboards] only — NOT storyboardsLoading. Including the
  // loading flag (which we set true inside) would re-run the effect mid-fetch,
  // whose cleanup flips `cancelled` and drops the in-flight result, leaving the
  // list stuck empty + spinning.
  useEffect(() => {
    if (mode !== 'script' || storyboards !== null) return undefined;
    let cancelled = false;
    setStoryboardsLoading(true);
    setStoryboardsError(null);
    (async () => {
      const resp = await videoEditingService.listSentScripts();
      if (cancelled) return;
      if (!resp) {
        setStoryboardsError('Failed to load scripts. Try again.');
        setStoryboards([]);
      } else {
        setStoryboards(resp.scripts);
      }
      setStoryboardsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [mode, storyboards]);

  function scriptLoadLabel(script: SentScript, result: ResolvedSource): string {
    const product = script.productName || script.scriptName || 'Script';
    const chosen = (result.deliveries || []).find(delivery => delivery.deliveryId === result.deliveryId);
    const who = chosen?.creatorName || chosen?.scriptName;
    return who ? `${product} → ${who}` : product;
  }

  async function doResolve(script: SentScript, deliveryId?: string) {
    const requestId = resolveRequestId.current + 1;
    resolveRequestId.current = requestId;
    setResolving(true);
    setResolveError(null);
    try {
      const result = await videoEditingService.resolveSource({ storyboardId: script.storyboardId, deliveryId });
      if (requestId !== resolveRequestId.current) return;
      setResolved(result);
      if (result.folderId) {
        onLoadClips({
          sourceType: 'STORYBOARD',
          folderId: result.folderId,
          folderUrl: result.folderUrl,
          storyboardId: script.storyboardId,
          deliveryId: result.deliveryId,
          label: scriptLoadLabel(script, result),
        });
      }
    } catch (err) {
      if (requestId !== resolveRequestId.current) return;
      setResolveError(errorMessage(err));
      setResolved(null);
    } finally {
      if (requestId === resolveRequestId.current) setResolving(false);
    }
  }

  function handleSelectStoryboard(sb: SentScript | null) {
    resolveRequestId.current += 1;
    setResolving(false);
    setSelectedStoryboard(sb);
    setResolved(null);
    setResolveError(null);
    if (sb) void doResolve(sb);
  }

  const deliveries = resolved?.deliveries || [];
  const needsDeliveryChoice = !resolved?.folderId && deliveries.length > 1;

  function handleLoad() {
    const trimmed = driveUrl.trim();
    if (trimmed) onLoadClips({ sourceType: 'DRIVE', driveUrl: trimmed, label: trimmed });
  }

  const canLoad = driveUrl.trim().length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      {/* Saved projects — open a project directly, independent of locating assets.
          Picking one loads its Drive folder's clips AND opens that project. */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Autocomplete
          size="small"
          sx={{ width: 560, maxWidth: '100%' }}
          loading={projectsLoading}
          options={visibleProjects}
          // Already text-matched above (so the hidden count can be derived from the same
          // pass); tell the Autocomplete not to filter a second time.
          filterOptions={(o) => o}
          inputValue={projectInput}
          onInputChange={(_e, value) => setProjectInput(value)}
          value={selectedProject}
          onChange={(_e, value) => handleSelectProject(value)}
          groupBy={projectGroup}
          getOptionLabel={projectLabel}
          isOptionEqualToValue={(a, b) => a.planId === b.planId}
          // Says what the filter is hiding, right where the user is looking. Without it
          // a project marked Ready looks gone rather than filtered.
          noOptionsText={hiddenReadyCount > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                No projects in progress
                {projectInput.trim() ? ' match' : ''} — but {hiddenReadyCount} ready {hiddenReadyCount === 1 ? 'project' : 'projects'} {hiddenReadyCount === 1 ? 'is' : 'are'} hidden.
              </Typography>
              {showReadyButton}
            </Box>
          ) : 'No projects'}
          PaperComponent={({ children, ...paperProps }) => (
            <Paper {...paperProps}>
              {children}
              {/* Only when the search DID turn something up, so this and noOptionsText
                  never both speak. */}
              {hiddenReadyCount > 0 && visibleProjects.length > 0 && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {hiddenReadyCount} ready {hiddenReadyCount === 1 ? 'project' : 'projects'} hidden
                    </Typography>
                    {showReadyButton}
                  </Box>
                </>
              )}
            </Paper>
          )}
          renderOption={(props, p) => (
            <li {...props} key={p.planId}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{projectLabel(p)}</Typography>
                  {/* Only on finished ones — a chip on every row would be noise, and
                      "in progress" is the assumption this field is built around. */}
                  {isReadyProject(p) && <Chip size="small" label="Ready" color="success" variant="outlined" />}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {p.sourceType === 'STORYBOARD' ? 'Script' : 'Drive folder'} · updated {new Date(p.updatedAt).toLocaleDateString()}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Open a project"
              placeholder={statusFilter === 'drafts' ? 'Search projects in progress' : 'Search all projects'}
            />
          )}
        />
        {creators.length > 1 && (
          <TextField
            select
            size="small"
            label="Creator"
            value={creatorFilter}
            onChange={(e) => setCreatorFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All users</MenuItem>
            {creators.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
        )}
        {/* Hidden while it would do nothing — same reason the Creator filter waits for a
            second creator. The second clause keeps a non-default choice reachable, so the
            control can never vanish while it is still filtering something out. */}
        {(readyCount > 0 || statusFilter !== 'drafts') && (
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="drafts">Drafts</MenuItem>
            <MenuItem value="ready">Ready</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        )}
        </Box>
      </Box>

      <Divider textAlign="left" sx={{ mb: 2, color: 'text.secondary', '&::before': { width: 0 }, '& .MuiDivider-wrapper': { pl: 0 } }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          or start a new project
        </Typography>
      </Divider>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_e, next) => { if (next) setMode(next); }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="drive">Google Drive folder</ToggleButton>
        <ToggleButton value="script">Sent scripts</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'drive' ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Drive folder link or id"
            placeholder="https://drive.google.com/drive/folders/…"
            value={driveUrl}
            onChange={e => setDriveUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canLoad && !loadingClips) handleLoad(); }}
            size="small"
            fullWidth
            sx={{ maxWidth: 560 }}
          />
          <Button
            variant="contained"
            onClick={handleLoad}
            disabled={!canLoad || loadingClips}
            startIcon={loadingClips ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Load clips
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Autocomplete
              size="small"
              sx={{ width: 560, maxWidth: '100%' }}
              loading={storyboardsLoading}
              options={storyboards || []}
              value={selectedStoryboard}
              onChange={(_e, value) => handleSelectStoryboard(value)}
              // Label includes both script name and product so typing filters on either.
              getOptionLabel={(o) => `${o.scriptName || 'Untitled'}${o.productName ? ` · ${o.productName}` : ''}`}
              isOptionEqualToValue={(o, v) => o.storyboardId === v.storyboardId}
              renderOption={(props, o) => (
                <li {...props} key={o.storyboardId}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.scriptName || 'Untitled script'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.productName ? `${o.productName} · ` : ''}Sent {o.deliveryCount}× · {new Date(o.lastSentAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sent script"
                  placeholder="Choose a script"
                />
              )}
            />
          </Box>

          {storyboardsError && <Alert severity="error" sx={{ mt: 1.5 }}>{storyboardsError}</Alert>}

          {resolving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Finding this script&apos;s upload folder…</Typography>
            </Box>
          )}

          {resolveError && <Alert severity="warning" sx={{ mt: 1.5 }}>{resolveError}</Alert>}

          {/* Multiple creators → let the editor choose whose folder to work on. */}
          {needsDeliveryChoice && (
            <TextField
              select
              size="small"
              label="This script was sent to several creators — pick one"
              value={resolved?.deliveryId || ''}
              onChange={e => { if (selectedStoryboard) void doResolve(selectedStoryboard, e.target.value); }}
              sx={{ mt: 2, width: 560, maxWidth: '100%' }}
            >
              {deliveries.map(d => (
                <MenuItem key={d.deliveryId} value={d.deliveryId}>
                  {d.creatorName || 'Unknown creator'} · {new Date(d.createdAt).toLocaleDateString()}
                </MenuItem>
              ))}
            </TextField>
          )}

          {resolved?.folderUrl && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <Link href={resolved.folderUrl} target="_blank" rel="noopener">Open upload folder in Google Drive</Link>
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}
